/**
 * Deferred Video - Custom Element
 * 客户端判断设备，只加载匹配当前设备的视频
 * 防止重复加载，优化性能
 */

(function() {
  'use strict';

  let isInitCompleted = false;

  class DeferredVideo extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
      this.loading = false;
      this.poster = null;
      this.template = null;
      this.video = null;
      this.observer = null;
      this.loadTimeout = null;
      this.posterHidden = false;
      this.videoReady = false;
      this.readyCheckInterval = null;
      this.isSkipped = false;
      this._initialized = false;
      this._loadAttempted = false;
      this._destroyed = false;
    }

    // 核心：判断当前设备是否匹配
    shouldLoad() {
      const videoType = this.dataset.videoType;
      if (!videoType) return true;
      
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      
      if (videoType === 'desktop' && isMobile) {
        console.log(`[DeferredVideo] ${videoType} skipped on mobile`);
        return false;
      }
      if (videoType === 'mobile' && !isMobile) {
        console.log(`[DeferredVideo] ${videoType} skipped on desktop`);
        return false;
      }
      
      return true;
    }

    connectedCallback() {
      // 防止重复执行
      if (this._initialized) {
        console.log('[DeferredVideo] Already initialized, skipping');
        return;
      }
      this._initialized = true;

      console.log('[DeferredVideo] Connected:', this.dataset.blockId, this.dataset.videoType);
      
      // 立即判断设备，不匹配则直接移除
      if (!this.shouldLoad()) {
        console.log(`[DeferredVideo] ${this.dataset.videoType} removed - device mismatch`);
        this.isSkipped = true;
        this._destroyed = true;
        if (this.parentNode) {
          this.style.display = 'none';
          this.parentNode.removeChild(this);
        }
        return;
      }
      
      this.poster = this.querySelector('.video-poster');
      this.template = this.querySelector('template');
      
      if (!this.template) {
        console.warn('[DeferredVideo] No template found');
        return;
      }

      const shouldLoadImmediately = this.isLcpCandidate();
      
      if (shouldLoadImmediately) {
        this.loadVideo();
      } else {
        this.initLazyLoad();
      }
    }

    isLcpCandidate() {
      if (this.dataset.lcp === 'true') return true;
      if (this.poster && this.poster.getAttribute('fetchpriority') === 'high') return true;
      
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < viewportHeight && rect.bottom > 0) return true;
      
      return false;
    }

    initLazyLoad() {
      if (this._destroyed) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this._destroyed) {
              this.loadVideo();
              this.observer.disconnect();
            }
          });
        }, { 
          rootMargin: '200px',
          threshold: 0.1
        });
        this.observer.observe(this);
      }

      const loadFallback = () => {
        if (!this.loaded && !this.loading && !this.isSkipped && !this._destroyed) {
          this.loadVideo();
        }
      };

      if (document.readyState === 'complete') {
        this.loadTimeout = setTimeout(loadFallback, 3000);
      } else {
        window.addEventListener('load', () => {
          this.loadTimeout = setTimeout(loadFallback, 2000);
        }, { once: true });
      }
    }

    loadVideo() {
      // 如果已销毁，不加载
      if (this._destroyed) {
        console.log('[DeferredVideo] Destroyed, skipping load');
        return;
      }

      // 加载前检查设备匹配
      if (!this.shouldLoad()) {
        console.log(`[DeferredVideo] ${this.dataset.videoType} skipped in loadVideo`);
        this.isSkipped = true;
        this._destroyed = true;
        if (this.parentNode) {
          this.parentNode.removeChild(this);
        }
        return;
      }
      
      // 防止重复加载尝试
      if (this._loadAttempted) {
        console.log('[DeferredVideo] Load already attempted, skipping');
        return;
      }
      this._loadAttempted = true;
      
      if (this.loaded || this.loading) {
        console.log('[DeferredVideo] Already loaded or loading, skipping');
        return;
      }
      
      // 检查是否已经有 video 元素
      if (this.querySelector('video')) {
        console.log('[DeferredVideo] Video element already exists, skipping');
        this.loaded = true;
        return;
      }
      
      if (!this.template) {
        console.warn('[DeferredVideo] No template, skipping');
        return;
      }
      
      this.loading = true;

      console.log('[DeferredVideo] Loading video...', this.dataset.videoType);

      const templateContent = this.template.content.cloneNode(true);
      this.appendChild(templateContent);

      this.video = this.querySelector('video');
      if (!this.video) {
        this.loading = false;
        console.warn('[DeferredVideo] No video element found in template');
        return;
      }

      this.loaded = true;
      this.loading = false;

      this.video.setAttribute('playsinline', 'true');
      this.video.setAttribute('muted', 'true');
      this.video.removeAttribute('autoplay');
      this.video.removeAttribute('poster');
      this.video.poster = '';
      
      this.video.style.cssText = `
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        z-index: 5 !important;
        opacity: 0 !important;
        visibility: hidden !important;
        display: block !important;
        background: transparent !important;
        background-color: transparent !important;
        pointer-events: none !important;
      `;
      
      this.video.load();
      this.setupVideoEvents();
    }

    setupVideoEvents() {
      if (!this.video || this._destroyed) return;

      var self = this;

      var onVideoReady = function() {
        if (self.videoReady || self._destroyed) return;
        self.videoReady = true;
        
        console.log('[DeferredVideo] Video ready, preparing transition');

        if (self.readyCheckInterval) {
          clearInterval(self.readyCheckInterval);
          self.readyCheckInterval = null;
        }

        self.video.pause();
        self.video.currentTime = 0;
        
        requestAnimationFrame(function() {
          if (self._destroyed || !self.video) return;
          
          self.video.style.visibility = 'visible';
          self.video.style.opacity = '1';
          self.video.style.transition = 'opacity 0.8s ease-in-out';
          self.video.classList.add('visible');
          
          void self.video.offsetHeight;

          setTimeout(function() {
            if (self._destroyed || !self.video) return;
            self.hidePoster();
            
            setTimeout(function() {
              if (self._destroyed || !self.video) return;
              self.video.play().catch(function() {});
            }, 300);
          }, 400);
        });
      };

      var events = ['canplay', 'loadeddata', 'loadedmetadata'];
      events.forEach(function(event) {
        self.video.addEventListener(event, onVideoReady, { once: true });
      });

      if (this.video.readyState >= 3) {
        onVideoReady();
      }

      this.video.addEventListener('error', function() {
        console.warn('[DeferredVideo] Video error');
        if (self._destroyed || !self.video) return;
        self.video.style.visibility = 'visible';
        self.video.style.opacity = '1';
        self.hidePoster();
        self.video.play().catch(function() {});
      }, { once: true });

      this.readyCheckInterval = setInterval(function() {
        if (self.videoReady || self._destroyed) {
          if (self.readyCheckInterval) {
            clearInterval(self.readyCheckInterval);
            self.readyCheckInterval = null;
          }
          return;
        }
        if (self.video && self.video.readyState >= 3) {
          console.log('[DeferredVideo] ReadyState check triggered');
          onVideoReady();
        }
      }, 100);

      setTimeout(function() {
        if (!self.videoReady && !self._destroyed && self.video) {
          console.log('[DeferredVideo] Force show after 5s');
          self.video.style.visibility = 'visible';
          self.video.style.opacity = '1';
          self.hidePoster();
          self.video.play().catch(function() {});
        }
      }, 5000);
    }

    hidePoster() {
      if (this.posterHidden || this._destroyed) return;
      this.posterHidden = true;

      if (!this.poster) return;

      console.log('[DeferredVideo] Hiding poster');

      this.poster.style.transition = 'opacity 0.6s ease-in-out';
      this.poster.style.opacity = '0';
      this.poster.classList.add('video-poster--hidden');
      
      setTimeout(function() {
        if (this.poster) {
          this.poster.style.display = 'none';
        }
      }.bind(this), 600);
    }

    disconnectedCallback() {
      this._destroyed = true;
      
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }
      if (this.readyCheckInterval) {
        clearInterval(this.readyCheckInterval);
        this.readyCheckInterval = null;
      }
    }
  }

  // 注册自定义元素
  if (!customElements.get('deferred-video')) {
    customElements.define('deferred-video', DeferredVideo);
    console.log('[DeferredVideo] Registered');
  }

  function initDeferredVideos() {
    if (isInitCompleted) {
      console.log('[DeferredVideo] Init already completed');
      return;
    }
    isInitCompleted = true;
    
    console.log('[DeferredVideo] Initializing all instances');
    document.querySelectorAll('deferred-video').forEach(function(el) {
      if (el._initialized || el.loaded) {
        console.log('[DeferredVideo] Instance already initialized');
        return;
      }
      el.connectedCallback();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeferredVideos);
  } else {
    initDeferredVideos();
  }

  window.initDeferredVideos = initDeferredVideos;

})();